/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { UiController } from 'modules/common/ui-controller';
import variants from 'modules/common/theme/variants';
import {
  selectAppTheme,
  selectTourRun,
  selectTourSteps,
  selectTourTarget,
} from 'modules/dashboard/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from '../../../dashboard/slice';
import { tourStyles } from './styles';

const JoyrideProvider = () => {
  const dispatch = useDispatch();
  const run = useSelector(selectTourRun);
  const steps = useSelector(selectTourSteps);
  const target = useSelector(selectTourTarget);
  const themeName = useSelector(selectAppTheme);

  const selectedVariant = variants.find((variant) => variant.name === themeName);
  const [stepsArrayFormatted, setStepsArrayFormatted] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (steps?.length > 0) {
      const stepFormatted = steps?.map((step) => ({
        ...step,
        disableBeacon: true,
      }));
      setStepsArrayFormatted(stepFormatted);
    }
  }, [steps]);

  useEffect(() => {
    if (steps.length > 0) {
      setStepIndex(0);
      requestAnimationFrame(() => setIsReady(true));
    }
  }, [steps]);
  //

  const waitForElement = (selector, timeout = 3000) =>
    new Promise((resolve, reject) => {
      const startTime = Date.now();

      const interval = setInterval(() => {
        const element = document.querySelector(selector);
        if (element) {
          clearInterval(interval);
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(interval);
          reject(new Error(`Target ${selector} not found after ${timeout}ms`));
        }
      }, 100); // Check every 100ms
    });

  const handleCallback = async (data) => {
    const { type, index, status, action } = data;

    // 1. Handle the "Next" action universally
    if (type === 'step:after' && action === 'next') {
      const nextIndex = index + 1;
      const nextStep = stepsArrayFormatted[nextIndex];

      // CHECK: Is this the last step?
      const isLastStep = index === stepsArrayFormatted.length - 1;

      if (isLastStep) {
        // If it's the last step, don't wait for anything.
        // Just let the tour finish.
        dispatch(dashboardActions.resetTour());
        setStepIndex(0);
        setIsReady(false);
        return;
      }

      // Check if there is a next step to go to
      if (nextStep) {
        // 1b. Pause Joyride and wait for the next target to appear in DOM

        setIsReady(false);
        // 1a. If this specific step triggers a tab change, fire it now
        // (You can still use your index-based logic or a metadata flag)
        if (index === 0) {
          console.log('Step 0 finished, switching tab now...');
          UiController.setTab(target);
        }
        if (index === 2) {
          dispatch(dashboardActions.setTabChange(false));
        }

        try {
          // This will resolve instantly if the element is already there,
          // or wait up to 3 seconds if it's mounting.
          await waitForElement(nextStep.target, 3000);

          setStepIndex(nextIndex);
          setIsReady(true);
        } catch (error) {
          console.warn('Could not find next target, staying here or skipping.', error);
          // Fallback: you could move to the next index anyway or stop the tour
          setIsReady(true);
        }
        return; // Prevent the default increment logic below
      }
    }

    // 2. Handle "Back" action (usually doesn't need waiting)
    if (type === 'step:after' && action === 'prev') {
      setStepIndex(index - 1);
    }

    // 3. Handle End of Tour
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      dispatch(dashboardActions.resetTour());
      setStepIndex(0);
      setIsReady(false);
    }
  };
 //
  return (
    <Joyride
      stepIndex={stepIndex}
      steps={stepsArrayFormatted}
      run={run && isReady}
      locale={{
        last: 'End',
      }}
      continuous
      // disableScrolling
      disableScrollParentFix
      disableOverlayClose
      scrollOffset={150} 
      scrollDuration={300}
      callback={handleCallback}
      styles={tourStyles(selectedVariant)}
      floaterProps={{
        disableAnimation: true,
      }}
    />
  );
};

export default JoyrideProvider;
